import styled from '@emotion/styled'
import { AutocompleteProps, CircularProgress, InputBase, Autocomplete as MuiAutocomplete } from '@mui/material'
import { debounce } from '@mui/material/utils'
import { Stack } from '@mui/system'
import React, { useEffect } from 'react'
import { Search as SearchIcon } from 'react-feather'
import useAuth from '../hooks/useAuth'
import { formatDateYYYYMMDD } from '../packages/utils/numberFormatters'
import { normalizedName } from './entities/CompanyName'
import {
    ABBLabel,
    FELabel,
    GELabel,
    IPOLabel,
    NonCashIssueLabel,
    RELabel,
    HasWarrantLabel,
    SPECLabel,
} from './entities/IssueTypeLabels'
import SearchApi from '../api/SearchApi'
import { SearchEntity } from 'types/SearchEntity'

const SearchIconWrapper = styled.div`
    width: 50px;
    height: 100%;
    position: absolute;
    pointer-events: none;
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
        width: 22px;
        height: 22px;
    }
`

const Input = styled(InputBase)`
    color: inherit;
    background-color: #f1f1f1;
    width: 100%;

    > input {
        color: ${(props) => props.theme.header.search.color};
        padding-top: ${(props) => props.theme.spacing(2.5)};
        padding-right: ${(props) => props.theme.spacing(2.5)};
        padding-bottom: ${(props) => props.theme.spacing(2.5)};
        padding-left: ${(props) => props.theme.spacing(12)};
        width: 160px;
    }
`

const dateBasedOnType = (option: SearchOption) => {
    if (option.issueType === 'Rights issue') {
        return option.subEndDate
    } else {
        return option.publicationDate
    }
}

export const issueTags = (
    option: Pick<SearchOption, 'issueType' | 'sharesInUnit' | 'unitExtraContent'> & { hasWarrants: boolean },
) => {
    const { issueType, hasWarrants, unitExtraContent, sharesInUnit } = option
    const tags = []

    const parsedSharesInUnit = sharesInUnit === null ? null : parseInt(sharesInUnit)
    const isSpecialUnitIssue = unitExtraContent || parsedSharesInUnit === 0

    if (issueType) {
        // TODO replace these with icon tags
        if (issueType === 'Rights issue') {
            tags.push(<FELabel key='FE' />)
        } else if (issueType === 'Directed issue') {
            tags.push(<RELabel key='RE' />)
        } else if (issueType === 'ABB Directed issue') {
            tags.push(<ABBLabel key='ABB' />)
        } else if (issueType === 'Non-cash issue') {
            tags.push(<NonCashIssueLabel key='nonCash' />)
        } else if (issueType === 'IPO') {
            tags.push(<IPOLabel key='IPO' />)
        } else if (issueType === 'Guarantor fee issue') {
            tags.push(<GELabel key='GE' />)
        }

        if (isSpecialUnitIssue) {
            tags.push(<SPECLabel key='SPEC' />)
        }

        if (tags.length === 0) {
            tags.push(<>[no tag]</>)
        }
    }
    if (hasWarrants && !isSpecialUnitIssue) {
        tags.push(<HasWarrantLabel key='TO' />)
    }
    tags.forEach((tag, index) => {
        tags[index] = <React.Fragment key={index}>{tag}</React.Fragment>
    })
    return tags
}

const renderOption = (props: React.HTMLAttributes<HTMLLIElement>, option: SearchOption) => {
    const numberOfWarrants =
        option.numWarrants === null || option.numWarrants === undefined ? null : parseInt(option.numWarrants)
    if (option.type === 'issue') {
        return (
            <li {...props} key={option.id}>
                <div tabIndex={0} style={{ marginLeft: '0px' }}>
                    <Stack alignItems='center' direction='row' spacing={2} sx={{ display: 'flex' }}>
                        <span>{option.name}</span>{' '}
                        <Stack alignItems='center' direction='row' spacing={2} sx={{ display: 'flex' }}>
                            {issueTags({ ...option, hasWarrants: !!numberOfWarrants })}
                        </Stack>{' '}
                        <span>{formatDateYYYYMMDD(dateBasedOnType(option))}</span>
                    </Stack>
                </div>
            </li>
        )
    }

    if (option.type === 'warrant') {
        return (
            <li {...props} key={option.id}>
                <div tabIndex={0} style={{ marginLeft: '0px' }}>
                    <Stack alignItems='center' direction='row' spacing={2} sx={{ display: 'flex' }}>
                        <span>{option.name}</span> <HasWarrantLabel key='TO' />
                        <span>{formatDateYYYYMMDD(option.subEndDate)}</span>
                    </Stack>
                </div>
            </li>
        )
    }

    return (
        <li {...props} style={{ marginLeft: '0px' }} key={option.id}>
            <div tabIndex={0}>{option.name}</div>
        </li>
    )
}

const typeI18n: Record<SearchEntity['type'], string> = {
    issue: 'Emissioner',
    warrant: 'Optioner',
    company: 'Bolag',
    investor: 'Investerare',
    convertible: 'Konvertibler',
}

const searchApi = new SearchApi()

export type SearchOption = Omit<SearchEntity, 'type'> & {
    type: SearchEntity['type'] | 'no-match'
}

function Autocomplete({
    onChange,
    placeholder,
    searchOnlyFor,
    ...props
}: Omit<AutocompleteProps<SearchOption, false, false, true>, 'onChange' | 'renderInput' | 'options'> & {
    onChange: (option: SearchEntity | null) => void
    placeholder: string
    searchOnlyFor?: SearchEntity['type']
}) {
    const { isAuthenticated } = useAuth()
    const [internalInputValue, setInternalInputValue] = React.useState('')
    const [options, setOptions] = React.useState<SearchOption[]>([])
    const [isLoading, setIsLoading] = React.useState(false)
    const inputValue = props.inputValue || internalInputValue

    const fetch = React.useMemo(
        () =>
            debounce(async (request: { input: string }, callback: (result: SearchEntity[]) => void) => {
                const issues = await searchApi.freeSearch(request.input)
                callback(issues)
            }, 400),
        [],
    )

    useEffect(() => {
        if (!isAuthenticated) return

        let active = true

        if (inputValue === '') {
            setOptions([])
            return undefined
        }

        setIsLoading(true)

        void fetch({ input: inputValue }, (results) => {
            if (active) {
                const newOptions: SearchOption[] = []

                for (let i = 0; i < results.length; i++) {
                    switch (results[i].type) {
                        case 'company':
                            newOptions.push({
                                ...results[i],
                                name: normalizedName(results[i].name),
                            })
                            break
                        default:
                            newOptions.push(results[i])
                    }
                }

                // Sort by the custom order for types, then by name
                const groupOrder = { company: 1, issue: 2, warrant: 3, investor: 4, convertible: 5, 'no-match': 0 }
                newOptions.sort((a, b) => {
                    const groupComparison = (groupOrder[a.type] || 99) - (groupOrder[b.type] || 99)
                    if (groupComparison !== 0) return groupComparison
                    if (a.type === 'warrant' && b.type === 'warrant') return 0
                    return a.name?.localeCompare(b.name) || 0
                })

                setOptions(newOptions)
                setIsLoading(false)
            }
        })

        return () => {
            active = false
        }
    }, [inputValue, fetch, isAuthenticated])

    const handleChange = (event: unknown, searchOption: SearchOption | string | null) => {
        if (searchOption === null) {
            onChange(null)
            return
        }

        if (typeof searchOption === 'string') {
            console.warn('Unexpected searchOption of type `string`.')
            return
        }

        onChange(searchOption as SearchEntity)
        setInternalInputValue('')
    }

    // https://mui.com/material-ui/react-autocomplete/#google-maps-place

    // Kolla här för gruppering av resultaten: https://refine.dev/blog/material-ui-autocomplete-component/#groupby

    return (
        <MuiAutocomplete<SearchOption, false, false, true>
            autoHighlight
            clearOnEscape
            id='search'
            inputValue={inputValue}
            onChange={handleChange}
            onInputChange={(event, newInputValue, reason) => {
                console.log({ reason })
                // if (reason === 'reset') return setInputValue('')
                setInternalInputValue(newInputValue)
            }}
            freeSolo
            fullWidth
            getOptionDisabled={(o) => o.id === 'no-match'}
            options={options}
            filterOptions={(options) => {
                const filteredOptions = searchOnlyFor ? options.filter(({ type }) => type === searchOnlyFor) : options

                if (filteredOptions.length > 0) return filteredOptions
                if (!inputValue) return filteredOptions
                if (isLoading) return filteredOptions

                return [
                    {
                        id: 'no-match',
                        type: 'no-match',
                        typeLabel: null,
                        name: 'Inga träffar',
                        createdAt: new Date().toISOString(),
                        numWarrants: null,
                        issueType: null,
                        publicationDate: null,
                        sharesInUnit: null,
                        updatedAt: new Date().toISOString(),
                        subEndDate: null,
                        unitExtraContent: null,
                    },
                ]
            }}
            renderInput={(params) => (
                <Input
                    ref={params.InputProps.ref}
                    inputProps={params.inputProps}
                    placeholder={placeholder}
                    startAdornment={
                        <SearchIconWrapper>
                            <SearchIcon />
                        </SearchIconWrapper>
                    }
                    endAdornment={isLoading ? <CircularProgress size={15} sx={{ marginRight: 4 }} /> : null}
                />
            )}
            getOptionLabel={(option) => (typeof option === 'string' ? option : option.name || '')}
            renderOption={renderOption}
            groupBy={(option) => typeI18n[option.type] || option.type}
            noOptionsText='Inga träffar'
            renderGroup={(params) =>
                searchOnlyFor ? (
                    params.children
                ) : (
                    <li key={params.key} style={{ marginBottom: '8px' }}>
                        <span style={{ marginLeft: '16px', marginBottom: '4px', display: 'flex', fontWeight: 'bold' }}>
                            {params.group}
                        </span>
                        <ul style={{ paddingLeft: '16px' }}>{params.children}</ul>
                    </li>
                )
            }
            {...props}
        />
    )
}

export default Autocomplete
